/** @jsx jsx */
import { jsx } from "theme-ui"
import { useContext, useEffect, useRef } from "react"
import PropTypes from "prop-types"

import { FrcEnabledContext, FrcLanguageContext } from "./frc-context"

/**
 * Friendly Captcha (privacy-friendly, GDPR-compliant) widget for Gatsby forms.
 * Port of services/components/blocks/captcha.tsx. The widget runs a proof-of-work
 * challenge in the browser (no tracking, no cookies) and writes its result into a
 * hidden input named `frc-captcha-response`. The Form submit handler resolves
 * that value (./frc.js) and the shared Netlify mailer verifies it server-side
 * (functions/mailer.mjs).
 *
 * Uses the v2 SDK (`@friendlycaptcha/sdk`) — the v2 siteverify API the mailer
 * calls rejects solutions from the old v1 widget (`friendly-challenge`), so
 * widget and API version must match.
 *
 * The widget is created per mount with `createWidget` and destroyed on unmount.
 * The SDK's auto-attach script scans the page only once on load, so it would
 * miss every form reached through client-side navigation. The SDK is imported
 * lazily: pages without a form never download it, and nothing loads from a CDN.
 *
 * Placement: a Storyblok `captcha` blok renders the widget as `slot` where the
 * editor placed it (next to the submit button). `Form` always renders a
 * `fallback` at its end, which stays empty when the same form has a slot — so
 * every form gets exactly one widget.
 *
 * Env-gated: when GATSBY_FRC_SITEKEY is unset the widget renders nothing and the
 * form keeps working without a solution (the mailer skips verification when none
 * is sent).
 */
export const FRC_SITEKEY =
  (process.env.GATSBY_FRC_SITEKEY || "").trim() || undefined

let sdkPromise

/** One SDK per page — it coordinates all widgets through a shared agent iframe. */
function loadFrcSdk() {
  if (!sdkPromise) {
    sdkPromise = import("@friendlycaptcha/sdk").then(
      ({ FriendlyCaptchaSDK }) => new FriendlyCaptchaSDK()
    )
    sdkPromise.catch(() => {
      sdkPromise = undefined
    })
  }
  return sdkPromise
}

/**
 * The slot sits between the last form field and the submit button. The visible
 * gap above it is 41px — 25px trailing leading of the 40px-line checkbox label,
 * 8px field padding, 8px margin — so the gap below matches it.
 */
const SLOT_SPACING = { mt: 2, mb: "41px" }

/** True when a fallback shares its form with a slot, which then owns the widget. */
function isShadowedBySlot(element) {
  const form = element.closest("form")
  return !!(
    form && form.querySelector('.frc-captcha[data-frc-placement="slot"]')
  )
}

const FriendlyCaptcha = ({
  language: languageProp,
  placement = "fallback",
}) => {
  const widgetRef = useRef(null)
  const formLanguage = useContext(FrcLanguageContext)
  const enabled = useContext(FrcEnabledContext)
  const language = languageProp || formLanguage || "en"

  useEffect(() => {
    if (!FRC_SITEKEY || !enabled || !widgetRef.current) return undefined
    if (placement === "fallback" && isShadowedBySlot(widgetRef.current)) {
      return undefined
    }

    let widget
    let unmounted = false
    loadFrcSdk()
      .then((sdk) => {
        if (unmounted || !widgetRef.current) return
        widget = sdk.createWidget({
          element: widgetRef.current,
          sitekey: FRC_SITEKEY,
          startMode: "focus",
          language,
        })
      })
      .catch((error) => {
        // No widget means no token: the submit falls back to sending without one.
        console.error("[Friendly Captcha] widget could not be mounted", error)
      })

    return () => {
      unmounted = true
      if (widget) widget.destroy()
    }
  }, [enabled, language, placement])

  // Env-gated: without a site key there is nothing to load. A form that
  // requires a login switches the captcha off (FrcEnabledContext).
  if (!FRC_SITEKEY || !enabled) return null

  // startMode "focus": solving begins when the user focuses the form, so the
  // token is usually ready before they reach the submit button. The widget
  // brings its own Friendly Captcha branding and link.
  return (
    <div
      ref={widgetRef}
      className="frc-captcha"
      data-frc-placement={placement}
      // A fallback shadowed by a slot stays empty and must not add spacing.
      sx={{ "&:not(:empty)": placement === "slot" ? SLOT_SPACING : { my: 3 } }}
    />
  )
}

FriendlyCaptcha.propTypes = {
  language: PropTypes.string,
  placement: PropTypes.oneOf(["slot", "fallback"]),
}

export default FriendlyCaptcha
